import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import styled from 'styled-components'
import _ from 'lodash'
import { colours } from '@qflow/theme'

import { useProductGroupsLoader } from 'portal/components/RecordsModules/common'
import { getBrowserShortDateFormat, imperial } from 'portal/lib/functions'
import { ColourButton, icons } from 'portal/lib/primitives'

import { AddNewMilestone } from './AddNewMilestone'
import { AlertRecipients } from './AlertRecipients'
import {
  AdditionalText,
  AlertRuleContainer,
  Description,
  SettingArea,
  TextArea
} from './sharedStyledComponents'
import { EnableAlertRuleSwitch } from './EnableSwitch'

export const MaterialMilestones = ({
  projectId,
  alertKey,
  alertRules,
  setAlertRules,
  cannotEnable
}) => {
  const { error: productGroupsError } = useProductGroupsLoader(projectId)
  if (productGroupsError) {
    console.error('Error loading product groups:', productGroupsError)
  }

  const projectCountry = useSelector(
    (state: any) => state.projectDetails.modules?.country
  )
  const allProductGroups = useSelector((state: any) => state.productGroups)

  const isImperial = useMemo(() => imperial(projectCountry), [projectCountry])
  const WEIGHT_UNIT = useMemo(() => (isImperial ? 'lbs' : 't'), [isImperial])
  const VOLUME_UNIT = useMemo(
    () =>
      isImperial ? (
        'cu yd'
      ) : (
        <span>
          m<sup>3</sup>
        </span>
      ),
    [isImperial]
  )

  const columns = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'milestoneDate',
        key: 'milestoneDate',
        render: (text: any) =>
          text ? dayjs(text).format(getBrowserShortDateFormat()) : '',
        width: '20%'
      },
      {
        title: 'Product Group',
        dataIndex: 'productGroup',
        key: 'productGroup',
        render: (text: any, record: any) => record.productGroupDetails?.name
      },
      {
        title: 'Weight',
        dataIndex: 'weight',
        key: 'weight',
        render: (text: any) =>
          text ? (
            <span>
              {text} {WEIGHT_UNIT}
            </span>
          ) : (
            ''
          ),
        width: '20%'
      },
      {
        title: 'Volume',
        dataIndex: 'volume',
        key: 'volume',
        render: (text: any) =>
          text ? (
            <span>
              {text} {VOLUME_UNIT}
            </span>
          ) : (
            ''
          ),
        width: '20%'
      },
      {
        title: '',
        key: 'actions',
        render: (text: any, record: any) => (
          <div style={{ marginRight: '-0.25rem' }}>
            <Tooltip title="Remove milestone">
              <ColourButton.BasicBorderless
                name={`remove-milestone-${record.milestoneDate}-${record.productGroupDetails?.name?.replace(/\s+/g, '-').toLowerCase()}`}
                aria-label={'Remove milestone'}
                style={{
                  padding: '0.25rem 9px',
                  borderRadius: '50%',
                  color: colours.RED_DARKMODE
                }}
                onClick={() => {
                  const updatedAlertRules = _.cloneDeep(alertRules)
                  updatedAlertRules[alertKey].milestones = updatedAlertRules[
                    alertKey
                  ].milestones.filter(s => !_.isEqual(s, record))
                  setAlertRules(updatedAlertRules)
                }}
              >
                <icons.TrashCan colour="currentColor" />
              </ColourButton.BasicBorderless>
            </Tooltip>
          </div>
        ),
        width: '2rem'
      }
    ],
    [VOLUME_UNIT, WEIGHT_UNIT, alertKey, alertRules, setAlertRules]
  )

  type Milestone = {
    milestoneId: string
    productGroup: string
    productGroupDetails?: { id: string; name: string }
    milestoneDate: string
    weight?: number
    weightUnit?: string
    volume?: number
    volumeUnit?: string
  }

  const handleAddMilestone = (value: Milestone | null) => {
    if (!value) return

    if (value.productGroup) {
      const pgName =
        allProductGroups.data && Array.isArray(allProductGroups.data)
          ? allProductGroups.data.find(
              item => item.productGroupId === value.productGroup
            ).name
          : 'Unknown Product Group'
      const productGroupDetails = {
        id: value.productGroup,
        name: pgName
      }
      value.productGroupDetails = productGroupDetails
    }

    const updatedAlertRules = _.cloneDeep(alertRules)

    updatedAlertRules[alertKey] = {
      ...updatedAlertRules[alertKey]
    }
    updatedAlertRules[alertKey].milestones.push(value)

    // Sort milestones by date, then by product group name
    updatedAlertRules[alertKey].milestones.sort((a, b) => {
      if (a.milestoneDate !== b.milestoneDate) {
        return a.milestoneDate.localeCompare(b.milestoneDate)
      }
      if (a.productGroupDetails.id !== b.productGroupDetails.id) {
        return a.productGroupDetails.name.localeCompare(
          b.productGroupDetails.name
        )
      }
      if (a.weight !== b.weight) {
        return a.weight - b.weight
      }
      if (a.volume !== b.volume) {
        return a.volume - b.volume
      }
      return 0
    })
    setAlertRules(updatedAlertRules)
  }

  if (!alertRules[alertKey]) {
    return null
  }

  return (
    <>
      <AlertRuleContainer>
        <TextArea>
          <Description>Material Milestones</Description>
          <AdditionalText>
            On a specific date, get notified when the total related deliveries
            for a product group are below a set weight or volume.
          </AdditionalText>
        </TextArea>
        <SettingArea>
          <EnableAlertRuleSwitch
            alertKey={alertKey}
            alertRules={alertRules}
            setAlertRules={setAlertRules}
            cannotEnable={cannotEnable}
            ariaLabel="Enable Material Milestones"
          />
          <AddNewMilestone
            isImperial={isImperial}
            weightUnit={WEIGHT_UNIT}
            volumeUnit={VOLUME_UNIT}
            onAddMilestone={handleAddMilestone}
          />
          <MaterialMilestoneWrapper>
            <StyledTable
              rowKey={(record: any) => record.milestoneId}
              columns={columns}
              dataSource={alertRules[alertKey].milestones}
              locale={{
                emptyText: (
                  <div>
                    {!alertRules[alertKey].isEnabled ? (
                      'No milestones set'
                    ) : (
                      <span style={{ color: colours.RED_DARKMODE }}>
                        One or more milestones required
                      </span>
                    )}
                  </div>
                )
              }}
              pagination={false}
              size="small"
              showHeader={false}
            />
          </MaterialMilestoneWrapper>
        </SettingArea>
      </AlertRuleContainer>
      <AlertRecipients
        projectId={projectId}
        alertKey={alertKey}
        alertRules={alertRules}
        setAlertRules={setAlertRules}
      />
    </>
  )
}

const MaterialMilestoneWrapper = styled.div`
  margin-top: 1.25rem;
  min-width: 30rem;
`
const StyledTable = styled(Table)`
  tr > td.ant-table-cell-row-hover {
    background-color: ${colours.WHITE} !important;
  }
`
